import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import { api } from '../services/api.js';
import PageHeader from '../components/PageHeader.jsx';
import { Clipboard, Image, Smartphone, Search } from 'lucide-react';
import { formatDate } from '../lib/utils.js';
import { localDB } from '../services/db.js';

const CACHE_KEY = 'dashboard_counts';

const Card = ({ title, value, hint, icon: Icon, to }) => (
  <Link to={to} className="card flex items-center justify-between hover:bg-white/5">
    <div>
      <div className="text-xs uppercase tracking-wide text-zinc-400">{title}</div>
      <div className="text-2xl font-semibold">{value}</div>
      {hint && <div className="text-xs text-zinc-500">{hint}</div>}
    </div>
    <Icon className="text-accent" />
  </Link>
);

export default function Dashboard() {
  const { user, device } = useAuth();
  const [counts, setCounts] = useState({ clips: '-', snaps: '-', devices: '-' });

  const loadCounts = useCallback(async () => {
    // 1. Show cached counts immediately
    const cached = await localDB.getCache(CACHE_KEY);
    if (cached) {
      setCounts(cached);
    }

    // 2. Fetch fresh counts in background
    if (navigator.onLine) {
      try {
        const [c, s, d] = await Promise.all([
          api.listClips('limit=1'),
          api.listSnaps('limit=1'),
          api.listDevices()
        ]);
        const newCounts = {
          clips:   c?.data?.items?.length ?? 0,
          snaps:   s?.data?.items?.length ?? 0,
          devices: d?.data?.items?.length ?? 0,
        };
        setCounts(newCounts);
        await localDB.setCache(CACHE_KEY, newCounts);
      } catch { /* ignored - tiles are decorative */ }
    }
  }, []);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  return (
    <div>
      <PageHeader
        title={`Hi, ${user?.username} 👋`}
        subtitle={device ? `Active on ${device.device_name} (${device.device_type || device.platform || 'unknown'})` : 'Welcome to SyncVault'}
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Card title="Clipboard entries" value={counts.clips} icon={Clipboard} to="/app/clipboard" hint="Last 24h shown by default" />
        <Card title="Snapshots"        value={counts.snaps} icon={Image}     to="/app/snaps" />
        <Card title="Active devices"   value={counts.devices} icon={Smartphone} to="/app/devices" />
        <Card title="Search history"   value="Open"        icon={Search}    to="/app/search" hint="Find any clip or snap" />
      </div>

      <div className="card mt-6">
        <h2 className="mb-2 text-lg font-semibold">What you can do</h2>
        <ul className="grid grid-cols-1 gap-2 text-sm text-zinc-300 sm:grid-cols-2">
          <li>📋 Save clipboard text from any device.</li>
          <li>📸 Snap a photo, see it everywhere.</li>
          <li>🔍 Search text and image captions.</li>
          <li>🗄️ Items are auto-archived after 24h.</li>
        </ul>
      </div>
    </div>
  );
}
